import React, { useEffect, useMemo, useRef } from 'react'
import { useForm } from '@inertiajs/react'
import type { ActionItem, User } from '../../types'
import {
  ACTION_ITEM_SOURCE_KINDS,
  normalizeSourceKind,
  type ActionItemSourceKindValue,
  type ActionItemSourceOptions,
} from '../../constants/actionItemSources'
import ActionItemChecklist from './ActionItemChecklist'
import ActionItemHistory from './ActionItemHistory'
import { sortPeople } from '../../utils/peopleSort'
import {
  defaultActionItemOwnerId,
  NO_ELIGIBLE_OWNERS_MESSAGE,
  OWNER_PLACEHOLDER,
} from '../../utils/actionItemOwners'

interface ActionItemModalProps {
  isOpen: boolean
  onClose: () => void
  teamId: number
  teamMembers: User[]
  currentUserId: number
  existingItem?: ActionItem | null
  prefillTitle?: string
  sourceType?: string
  sourceId?: number
  sourceOptions?: ActionItemSourceOptions
}

function getEndOfWeek(): string {
  const d = new Date()
  const day = d.getDay()
  const diff = day === 0 ? 5 : 5 - day
  d.setDate(d.getDate() + (diff < 0 ? diff + 7 : diff))
  return d.toISOString().split('T')[0]
}

function getEndOfMonth(): string {
  const d = new Date()
  d.setMonth(d.getMonth() + 1, 0)
  return d.toISOString().split('T')[0]
}

function addDays(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toISOString().split('T')[0]
}

export default function ActionItemModal({
  isOpen, onClose, teamId, teamMembers, currentUserId,
  existingItem, prefillTitle, sourceType, sourceId, sourceOptions
}: ActionItemModalProps) {
  const titleRef = useRef<HTMLInputElement>(null)
  const isEditing = !!existingItem

  const defaultDueDate = addDays(7)
  const defaultOwnerId = defaultActionItemOwnerId(teamMembers, currentUserId)
  const hasEligibleOwners = teamMembers.length > 0
  const existingOwnerId = existingItem?.owner?.id != null
    ? String(existingItem.owner.id)
    : ''
  const initialOwnerId = isEditing ? existingOwnerId : defaultOwnerId
  const eligibleOwnerIds = teamMembers
    .map(member => String(member.id))
    .sort()
    .join(',')

  const initialKind = normalizeSourceKind(existingItem?.source_type || sourceType || '')
  const initialSourceId = initialKind ? String(existingItem?.source_id || sourceId || '') : ''

  const { data, setData, post, put, processing, reset, errors, transform, clearErrors } = useForm({
    title: existingItem?.title || prefillTitle || '',
    description: existingItem?.description || '',
    owner_id: initialOwnerId,
    due_date: existingItem?.due_date || defaultDueDate,
    source_kind: initialKind as ActionItemSourceKindValue | '',
    source_id: initialSourceId,
  })

  useEffect(() => {
    if (isOpen) {
      const kind = normalizeSourceKind(existingItem?.source_type || sourceType || '')
      setData({
        title: existingItem?.title || prefillTitle || '',
        description: existingItem?.description || '',
        owner_id: isEditing
          ? (existingItem?.owner?.id != null ? String(existingItem.owner.id) : '')
          : defaultActionItemOwnerId(teamMembers, currentUserId),
        due_date: existingItem?.due_date || defaultDueDate,
        source_kind: kind as ActionItemSourceKindValue | '',
        source_id: kind ? String(existingItem?.source_id || sourceId || '') : '',
      })
      clearErrors()
      setTimeout(() => titleRef.current?.focus(), 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingItem?.id])

  useEffect(() => {
    if (isEditing || !data.owner_id || isOwnerEligible(data.owner_id)) return

    setData(prev => {
      if (!prev.owner_id || isOwnerEligible(prev.owner_id)) return prev
      return {
        ...prev,
        owner_id: defaultActionItemOwnerId(teamMembers, currentUserId),
      }
    })
    // Keep drafts stable on heartbeat renders; only reconcile when eligible
    // owner IDs change. Editing always preserves its assigned owner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleOwnerIds, isEditing, currentUserId])

  const titleError = errors.title
  const descriptionError = errors.description
  const ownerError = errors.owner_id || (errors as Record<string, string>).owner
  const dueDateError = errors.due_date
  const sourceTypeError = (errors as Record<string, string>).source_type
  const sourceError = (errors as Record<string, string>).source
  const baseError = (errors as Record<string, string>).base
  const isOwnerEligible = (ownerId: string) =>
    teamMembers.some(member => String(member.id) === ownerId)

  const existingOwnerUnavailable =
    isEditing &&
    !!data.owner_id &&
    !teamMembers.some(member => String(member.id) === data.owner_id)

  const recordOptions = useMemo(() => {
    if (!data.source_kind) return []
    return sourceOptions?.[data.source_kind] ?? []
  }, [data.source_kind, sourceOptions])

  function handleKindChange(value: string) {
    const next = value as ActionItemSourceKindValue | ''
    setData(prev => ({ ...prev, source_kind: next, source_id: '' }))
  }

  function submitForm(createAnother: boolean) {
    transform(formData => ({
      action_item: {
        title: formData.title,
        description: formData.description,
        owner_id: formData.owner_id,
        due_date: formData.due_date,
        source_type: formData.source_kind || '',
        source_id: formData.source_kind ? formData.source_id : '',
      },
    }))

    const options = {
      preserveScroll: true,
      onSuccess: () => {
        if (createAnother) {
          setData(prev => ({ ...prev, title: '', description: '' }))
          clearErrors()
          setTimeout(() => titleRef.current?.focus(), 100)
        } else {
          reset()
          clearErrors()
          onClose()
        }
      }
    }

    if (isEditing) {
      put(`/teams/${teamId}/action_items/${existingItem!.id}`, options)
    } else {
      post(`/teams/${teamId}/action_items`, options)
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (
      !data.title.trim() ||
      !data.owner_id ||
      (!isEditing && !isOwnerEligible(data.owner_id)) ||
      (!!data.source_kind && !data.source_id)
    ) return
    submitForm(false)
  }

  function setDatePreset(date: string) {
    setData('due_date', date)
  }

  if (!isOpen) return null

  const submitDisabled =
    processing ||
    !data.title.trim() ||
    !data.owner_id ||
    (!isEditing && !isOwnerEligible(data.owner_id)) ||
    (!!data.source_kind && !data.source_id)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold" style={{ color: '#1B1B1B' }}>
            {isEditing ? 'Edit Action Item' : 'New Action Item'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F5F5F5]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {baseError && (
            <p className="text-sm text-status-off-track" role="alert">{baseError}</p>
          )}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Title</label>
            <input
              ref={titleRef}
              value={data.title}
              onChange={e => setData('title', e.target.value)}
              placeholder="What needs to be done?"
              aria-invalid={!!titleError}
              className={`w-full border rounded-md px-3 py-2 text-sm outline-none ${titleError ? 'border-status-off-track focus:border-status-off-track' : 'border-stone focus:border-charcoal'}`}
            />
            {titleError && (
              <p className="text-sm text-status-off-track mt-1">{titleError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
            <textarea
              value={data.description}
              onChange={e => setData('description', e.target.value)}
              placeholder="Add details..."
              rows={3}
              aria-invalid={!!descriptionError}
              className={`w-full border rounded-md px-3 py-2 text-sm outline-none resize-none ${descriptionError ? 'border-status-off-track focus:border-status-off-track' : 'border-stone focus:border-charcoal'}`}
            />
            {descriptionError && (
              <p className="text-sm text-status-off-track mt-1">{descriptionError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Owner</label>
            <select
              value={data.owner_id}
              onChange={e => setData('owner_id', e.target.value)}
              disabled={!hasEligibleOwners && !existingOwnerUnavailable}
              required
              aria-invalid={!!ownerError}
              className={`w-full border rounded-md px-3 py-2 text-sm outline-none ${ownerError ? 'border-status-off-track focus:border-status-off-track' : 'border-stone focus:border-charcoal'}`}
            >
              <option value="">{OWNER_PLACEHOLDER}</option>
              {existingOwnerUnavailable && existingItem?.owner && (
                // Keep a previously assigned owner visible while editing, but
                // do not make that owner eligible for new assignments.
                <option value={data.owner_id} disabled>{existingItem.owner.full_name}</option>
              )}
              {sortPeople(teamMembers).map(m => (
                <option key={m.id} value={m.id}>{m.full_name}</option>
              ))}
            </select>
            {ownerError && (
              <p className="text-sm text-status-off-track mt-1">{ownerError}</p>
            )}
            {!hasEligibleOwners && !existingOwnerUnavailable && !ownerError && (
              <p className="text-sm text-text-muted mt-1">{NO_ELIGIBLE_OWNERS_MESSAGE}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Due Date</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              <button type="button" onClick={() => setDatePreset(addDays(0))}
                className="text-sm px-2 py-1 rounded border border-gray-200 hover:bg-[#F8F9FA] text-text-secondary">Today</button>
              <button type="button" onClick={() => setDatePreset(addDays(7))}
                className="text-sm px-2 py-1 rounded border border-gray-200 hover:bg-[#F8F9FA] text-text-secondary">+7 days</button>
              <button type="button" onClick={() => setDatePreset(addDays(14))}
                className="text-sm px-2 py-1 rounded border border-gray-200 hover:bg-[#F8F9FA] text-text-secondary">+14 days</button>
              <button type="button" onClick={() => setDatePreset(getEndOfWeek())}
                className="text-sm px-2 py-1 rounded border border-gray-200 hover:bg-[#F8F9FA] text-text-secondary">End of Week</button>
              <button type="button" onClick={() => setDatePreset(getEndOfMonth())}
                className="text-sm px-2 py-1 rounded border border-gray-200 hover:bg-[#F8F9FA] text-text-secondary">End of Month</button>
            </div>
            <input
              type="date"
              value={data.due_date}
              onChange={e => setData('due_date', e.target.value)}
              aria-invalid={!!dueDateError}
              className={`w-full border rounded-md px-3 py-2 text-sm outline-none ${dueDateError ? 'border-status-off-track focus:border-status-off-track' : 'border-stone focus:border-charcoal'}`}
            />
            {dueDateError && (
              <p className="text-sm text-status-off-track mt-1">{dueDateError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Source</label>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={data.source_kind}
                onChange={e => handleKindChange(e.target.value)}
                aria-invalid={!!sourceTypeError}
                aria-label="Source kind"
                className={`w-full border rounded-md px-3 py-2 text-sm outline-none ${sourceTypeError ? 'border-status-off-track focus:border-status-off-track' : 'border-stone focus:border-charcoal'}`}
              >
                <option value="">No source</option>
                {ACTION_ITEM_SOURCE_KINDS.map(kind => (
                  <option key={kind.value} value={kind.value}>{kind.label}</option>
                ))}
              </select>
              <select
                value={data.source_id}
                onChange={e => setData('source_id', e.target.value)}
                disabled={!data.source_kind}
                aria-invalid={!!sourceError}
                aria-label="Source record"
                className={`w-full border rounded-md px-3 py-2 text-sm outline-none disabled:bg-[#F5F5F5] disabled:text-text-muted ${sourceError ? 'border-status-off-track focus:border-status-off-track' : 'border-stone focus:border-charcoal'}`}
              >
                <option value="">{data.source_kind ? 'Select record...' : '—'}</option>
                {recordOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
            {data.source_kind && recordOptions.length === 0 && (
              <p className="text-sm text-text-muted mt-1">No matching records in this organization.</p>
            )}
            {(sourceTypeError || sourceError) && (
              <p className="text-sm text-status-off-track mt-1">{sourceTypeError || sourceError}</p>
            )}
          </div>

          {isEditing && existingItem && (
            <div className="border-t border-gray-200 pt-4">
              <ActionItemChecklist
                actionItemId={existingItem.id}
                items={existingItem.checklist_items || []}
              />
            </div>
          )}

          {isEditing && existingItem && (
            <div className="border-t border-gray-200 pt-4">
              <ActionItemHistory item={existingItem} />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
            >
              Cancel
            </button>
            {!isEditing && (
              <button
                type="button"
                onClick={() => submitForm(true)}
                disabled={submitDisabled}
                className="px-4 py-2 text-sm font-medium rounded-md border disabled:opacity-50"
                style={{ borderColor: '#ED1C24', color: '#ED1C24' }}
              >
                Save and Create Another
              </button>
            )}
            <button
              type="submit"
              disabled={submitDisabled}
              className="px-4 py-2 text-sm text-white rounded-md disabled:opacity-50"
              style={{ backgroundColor: '#ED1C24' }}
            >
              {isEditing ? 'Save Changes' : 'Create Action Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
